import { Button, Collapse, Colors, Divider, Intent, Switch } from '@blueprintjs/core'
import * as React from 'react'
import CascadePicker from '../../../../components/Categories/CascadePicker'
import { GenericNumericInput } from '../../../../components/Inputs'
import Spinner from '../../../../components/Spinner'
import config from '../../../../config'
import { UprightStyle } from '../../../../types'
import { categoryMarketCategoryToPath } from '../../../../utils'
import BasicProductInfo from '../../components/BasicProductInfo'
import ListingStrategyPicker from '../../components/ListingStrategyPicker'
import EpidPicker from './EpidPicker'
import * as EbayInputs from './Inputs'
import SpecificRenderer from './Specifics/Renderer'
import { renderError, stylizeTitle } from '../../../products_v5/components/JoinedForm/Helpers'
import CHANNELS from '../../../../constants/Channels'
import { OrganizationShippingSettings } from '../../../../current/types'
import { ConditionDescriptor, ItemConditionPolicy } from '../../../products_v5/components/JoinedForm/types'
import { ConditionDescriptorPicker } from '../../../products_v5/components/JoinedForm/EbayCard/Inputs'
import './style.scss'

const COLUMNS: React.CSSProperties = {
  margin: '20px auto',
  display: 'grid',
  gridGap: '20px',
  gridTemplateColumns: 'repeat(5, 1fr)'
}

const style: UprightStyle = {
  specificHeading: { display: 'flex', gap: 4, alignItems: 'center' },
  specificError: { color: Colors.RED3, padding: '4px 0 20px' },
  marginUnderHundred: { marginBottom: '100px' }
}

interface Props {
  enableTaxTable?: boolean
  listingExists?: boolean
  savedStartTime?: Date
  form: any
  error?: { errors: any }
  listingStrategyId?: number
  listingStrategyDisabled?: boolean
  product: any
  requirements: any
  onCategoryChange: Function
  onListingStrategyChange: Function
  onFormChange: (key: string, value: any) => void
  onProductChange: Function
  onBestOfferChange: Function
  onProductDetailChange: Function
  onSpecificChange: Function
  onRefreshSpecifics: () => {}
  doOfferAdditionalInfo: boolean
  shippingSettings?: OrganizationShippingSettings
}

interface State {
  showNonRequiredSpecifics?: boolean
  categoryPickerOpen?: boolean
  setInitalShowNonRequiredSpecifics?: boolean
}

export default class Form extends React.Component<Props, State> {
  state = {
    categoryPickerOpen: false,
    showNonRequiredSpecifics: false,
    setInitalShowNonRequiredSpecifics: false
  }

  componentDidUpdate = () => {
    this.setAndCheckIfShowNonRequiredSpecifics()
  }

  setAndCheckIfShowNonRequiredSpecifics = () => {
    if (
      this.props.requirements.specifics.length > 0 &&
      Object.keys(this.props.form.specifics).length > 0 &&
      !this.state.setInitalShowNonRequiredSpecifics
    ) {
      let foundNonRequiredSpecifics = false
      const specificValueMap = this.props.form.specifics

      const specifics =
        (this.props.requirements.specifics &&
          this.props.requirements.specifics.map((s, i) => ({ ...s, originalIndex: i }))) ||
        []
      const nonRequiredSpecifics = specifics.filter(o => !o.required)

      Object.keys(specificValueMap).forEach(key => {
        if (nonRequiredSpecifics.find(s => s.name === key)) {
          if (specificValueMap[key] !== null && specificValueMap[key] !== undefined && specificValueMap[key] !== '')
            foundNonRequiredSpecifics = true
        }
      })

      this.setState({ showNonRequiredSpecifics: foundNonRequiredSpecifics, setInitalShowNonRequiredSpecifics: true })
    }
  }

  canModifyScheduleTime = () => {
    // Can always set schedule time if the listing is new
    if (!this.props.listingExists) return true

    // Can set schedule time if the listing exists but has no ebay start time
    if (!this.props.savedStartTime) return true

    // Can set schedule time if ebay start time is in the future
    if (this.props.savedStartTime > new Date()) return true

    return false
  }

  onListingTypeChange = value => {
    if (value === 'FixedPriceItem') {
      this.props.onFormChange('listingType', value)
      this.props.onFormChange('listingDuration', 'GTC')
    } else {
      this.props.onFormChange('listingType', value)
    }
  }

  renderSpecifics = () => {
    const { form, requirements } = this.props

    const specifics = requirements.specifics.map((s, i) => ({ ...s, originalIndex: i }))
    const nonRequiredSpecifics = specifics.filter(o => !o.required)
    const requiredSpecifics = specifics.filter(o => o.required)

    const hasRequirements = requirements.conditions.length > 0 || requirements.specifics.length > 0

    const specificValueMap = form.specifics

    if (requirements.loading) {
      return (
        <div>
          <Divider style={{ margin: '20px 0 10px' }} />
          <h4>Required Specifics</h4>
          <Spinner style={{ textAlign: 'left' }} />
        </div>
      )
    }

    return (
      <div style={{ position: 'relative', zIndex: 1 }}>
        <Divider style={{ margin: '20px 0 10px' }} />
        <div style={style.specificHeading}>
          <h4>Required Specifics</h4>
          {form.categoryMarketCategory && form.categoryMarketCategory.id && (
            <Button
              intent={Intent.PRIMARY}
              minimal
              onClick={() => this.setState(state => ({ showNonRequiredSpecifics: !state.showNonRequiredSpecifics }))}
            >
              {this.state.showNonRequiredSpecifics ? 'Hide' : 'Show'} {nonRequiredSpecifics.length} Optional Specifics
            </Button>
          )}
          <Button text="Refresh" minimal intent={Intent.PRIMARY} onClick={this.props.onRefreshSpecifics} />
        </div>
        {requirements.error && <div style={style.specificError}>{requirements.error}</div>}

        {hasRequirements ? (
          <div style={{ display: 'grid', gridGap: '30px', gridTemplateColumns: 'repeat(3, 1fr)' }}>
            {requirements.conditions.length > 0 && (
              <EbayInputs.ConditionPicker
                value={form.condition}
                options={requirements.conditions}
                onChange={v => {
                  // conditionDescription must be reset if the condition is any variant of "new" (value between 1000-1499)
                  // https://developer.ebay.com/devzone/guides/features-guide/content/Development/Desc-ItemCondition.html
                  if (v >= 1000 && v <= 1499) this.props.onFormChange('conditionDescription', '')

                  // condition descriptors (like "Ungraded Condition") are only applicable to a certain condition.
                  // Otherwise Ebay will complain if a user changes between conditions and leave behind some descriptors for the other condition.
                  this.props.onFormChange('conditionDescriptors', null)

                  return this.props.onFormChange('condition', v)
                }}
              />
            )}

            {requirements.itemConditionPolicies &&
              requirements.itemConditionPolicies
                .filter(
                  itemConditionPolicy =>
                    itemConditionPolicy.conditionId === form.condition?.toString() &&
                    itemConditionPolicy.conditionDescriptors
                )
                .map((itemConditionPolicy: ItemConditionPolicy) => {
                  return itemConditionPolicy.conditionDescriptors?.map((conditionDescriptor: ConditionDescriptor) => (
                    <ConditionDescriptorPicker
                      key={conditionDescriptor.conditionDescriptorId}
                      conditionDescriptor={conditionDescriptor}
                      formConditionDescriptors={form.conditionDescriptors}
                      onChange={(params: { name: string; value: any }) => {
                        this.props.onFormChange(params.name, params.value)
                      }}
                    />
                  ))
                })}

            <div style={style.marginUnderHundred}>
              <EbayInputs.ConditionDescription
                onChange={this.props.onFormChange}
                value={form.conditionDescription}
                condition={form.condition}
              />
            </div>

            {requiredSpecifics.map(ss => (
              <SpecificRenderer
                key={ss.name}
                createable={ss.validationRules.selectionMode === 'FreeText'}
                categoryMarketCategoryId={form.categoryMarketCategory.id}
                required={ss.required}
                value={specificValueMap[ss.name]}
                specificName={ss.name}
                onChange={(v, productRequired) => {
                  this.setState({ setInitalShowNonRequiredSpecifics: true })
                  this.props.onSpecificChange(v, ss.originalIndex, productRequired)
                }}
                constrainedBy={ss.constrainedBy}
                siblingValues={specificValueMap}
              />
            ))}
          </div>
        ) : (
          !requirements.error && <p style={{ fontStyle: 'italic' }}>Select category first</p>
        )}

        {nonRequiredSpecifics.length > 0 && (
          <div>
            <Collapse isOpen={this.state.showNonRequiredSpecifics} keepChildrenMounted>
              <div
                style={{ display: 'grid', gridGap: '30px', gridTemplateColumns: 'repeat(3, 1fr)', marginTop: '30px' }}
              >
                {nonRequiredSpecifics.map(ss => (
                  <SpecificRenderer
                    key={ss.name}
                    createable={ss.validationRules.selectionMode === 'FreeText'}
                    categoryMarketCategoryId={form.categoryMarketCategory.id}
                    required={false}
                    value={specificValueMap[ss.name]}
                    specificName={ss.name}
                    onChange={(v, productRequired) => this.props.onSpecificChange(v, ss.originalIndex, productRequired)}
                    constrainedBy={ss.constrainedBy}
                    siblingValues={specificValueMap}
                  />
                ))}
              </div>
            </Collapse>
          </div>
        )}

        {requirements.epidRequired && (
          <EpidPicker
            query={this.props.product.title}
            categoryMarketCategoryId={form.categoryMarketCategory.id}
            value={this.props.form.epid}
            onChange={v => this.props.onFormChange('epid', v)}
          />
        )}

        <div style={{ display: 'grid', gridGap: '30px', gridTemplateColumns: 'repeat(3, 1fr)', marginTop: '30px' }}>
          <EbayInputs.ProductListingDetail
            label="UPC"
            detail={this.props.requirements.upc}
            value={this.props.form.productListingDetails.upc}
            onChange={v => this.props.onProductDetailChange('upc', v)}
          />
          <EbayInputs.ProductListingDetail
            label="EAN"
            detail={this.props.requirements.ean}
            value={this.props.form.productListingDetails.ean}
            onChange={v => this.props.onProductDetailChange('ean', v)}
          />
          <EbayInputs.ProductListingDetail
            label="ISBN"
            detail={this.props.requirements.isbn}
            value={this.props.form.productListingDetails.isbn}
            onChange={v => this.props.onProductDetailChange('isbn', v)}
          />
        </div>
      </div>
    )
  }

  render() {
    const { form } = this.props
    const changeOrAdd = form.categoryMarketCategory && form.categoryMarketCategory.pathCache ? 'Change' : 'Add'

    const { listingStrategyDisabled, product, listingStrategyId, onListingStrategyChange } = this.props
    const isRepricing = product?.isMedia && product?.isRepricingPaused === false
    const isDisabled = listingStrategyDisabled || isRepricing

    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: '75%' }}>
          <h4>Product Info</h4>
          <div>
            {stylizeTitle('Category', this.props.error?.errors?.categoryMarketCategory)}{' '}
            {renderError(this.props.error?.errors?.categoryMarketCategory)}
            <Button
              intent={Intent.PRIMARY}
              minimal
              style={{ marginTop: -3 }}
              onClick={() => this.setState(state => ({ categoryPickerOpen: !state.categoryPickerOpen }))}
            >
              {this.state.categoryPickerOpen ? 'Hide' : changeOrAdd}
            </Button>
            <p className="bp3-text-muted">{form.categoryMarketCategory && form.categoryMarketCategory.pathCache}</p>
          </div>
          <Collapse isOpen={this.state.categoryPickerOpen}>
            <CascadePicker
              validateLeaf
              marketId={config.MARKETS.ebay}
              path={categoryMarketCategoryToPath(form.categoryMarketCategory)}
              onChange={this.props.onCategoryChange}
            />
          </Collapse>

          <EbayInputs.StoreCategoryPicker
            value={form.storeCategoryId}
            onChange={v => this.props.onFormChange('storeCategoryId', v)}
          />

          <BasicProductInfo
            isDisabled={false}
            onChange={this.props.onProductChange}
            channel={CHANNELS.EBAY}
            listingId={form.id}
            doOfferAdditionalInfo={this.props.doOfferAdditionalInfo}
            shippingSettings={this.props.shippingSettings}
            {...this.props.product}
          />
        </div>

        <Divider style={{ margin: '20px 0 10px' }} />
        <h4>Listing Strategy</h4>
        <div style={{ margin: '20px auto', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <ListingStrategyPicker
              onChange={v => onListingStrategyChange(v)}
              value={listingStrategyId}
              disabled={isDisabled}
              title="Listing Strategy"
            />
            {isRepricing && (
              <div style={{ marginTop: '1px', fontSize: '12px', color: '#666' }}>This is being repriced.</div>
            )}
          </div>
        </div>

        {this.renderSpecifics()}
        <Divider style={{ margin: '20px 0 10px' }} />
        <h4>Pricing and Shipping</h4>
        <div style={{ ...COLUMNS, gridTemplateColumns: 'repeat(5, 1fr)' }}>
          <EbayInputs.ListingType value={form.listingType} onChange={v => this.onListingTypeChange(v)} />
          <EbayInputs.ListingDuration
            value={form.listingDuration}
            onChange={v => this.props.onFormChange('listingDuration', v)}
            disabled={form.listingType === 'FixedPriceItem'}
          />
          <EbayInputs.StartingPrice
            value={form.startingPrice}
            onChange={v => this.props.onFormChange('startingPrice', v)}
            currencyCode={form.currencyCode}
            error={this.props.error?.errors?.startingPrice}
          />
          <div style={{ gridColumn: 'span 2' }}>
            <EbayInputs.ScheduleTimePicker
              value={form.scheduleTime}
              onChange={date => this.props.onFormChange('scheduleTime', date)}
              disabled={!this.canModifyScheduleTime()}
            />
          </div>
        </div>

        <div>
          <div style={{ ...COLUMNS, gridTemplateColumns: 'repeat(5, 1fr)' }}>
            <div>
              {form.listingType !== 'FixedPriceItem' && (
                <EbayInputs.BuyItNowPrice
                  value={form.buyItNowPrice}
                  onChange={v => this.props.onFormChange('buyItNowPrice', v)}
                  currencyCode={form.currencyCode}
                />
              )}
            </div>
            <div>
              {form.listingType !== 'FixedPriceItem' && (
                <EbayInputs.ReservePrice
                  value={form.reservePrice}
                  onChange={v => this.props.onFormChange('reservePrice', v)}
                  currencyCode={form.currencyCode}
                />
              )}
              {form.listingType === 'FixedPriceItem' && (
                <GenericNumericInput
                  title="Quantity"
                  value={form.quantity}
                  onChange={v => this.props.onFormChange('quantity', v)}
                  disabled={!!this.props.product.unitTracking} // TODO(product_inventory_unit): Handle this with better messaging when we support inventory units for normal listings
                  minorStepSize={1}
                />
              )}
            </div>
            <div>
              <GenericNumericInput
                title="Handling Time (Days)"
                value={form.dispatchTimeMax}
                onChange={v => this.props.onFormChange('dispatchTimeMax', v)}
                minorStepSize={1}
              />
            </div>
          </div>
          <div style={COLUMNS}>
            <div>
              <Switch
                disabled={!this.props.requirements.bestOfferEnabled}
                checked={form.bestOfferEnabled}
                label={
                  this.props.requirements.bestOfferEnabled
                    ? 'Allow Best Offer'
                    : 'Best Offer Unavailable for this Category'
                }
                onChange={() => this.props.onBestOfferChange(!form.bestOfferEnabled)}
              />
            </div>
          </div>
          <div style={{ ...COLUMNS, gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div>
              <EbayInputs.MinimumBestOfferPrice
                disabled={!this.props.requirements.bestOfferEnabled || !form.bestOfferEnabled}
                value={form.minimumBestOfferPrice}
                onChange={v => this.props.onFormChange('minimumBestOfferPrice', v)}
                currencyCode={form.currencyCode}
              />
            </div>
            <div>
              <EbayInputs.BestOfferAutoAcceptPrice
                disabled={!this.props.requirements.bestOfferEnabled || !form.bestOfferEnabled}
                value={form.bestOfferAutoAcceptPrice}
                onChange={v => this.props.onFormChange('bestOfferAutoAcceptPrice', v)}
                currencyCode={form.currencyCode}
              />
            </div>
          </div>

          <div>
            <EbayInputs.SellerProfiles
              shippingValue={form.shippingProfileId}
              returnValue={form.returnProfileId}
              paymentValue={form.paymentProfileId}
              marketAccountId={form.marketAccountId}
              onShippingChange={v => this.props.onFormChange('shippingProfileId', v)}
              onReturnChange={v => this.props.onFormChange('returnProfileId', v)}
              onPaymentChange={v => this.props.onFormChange('paymentProfileId', v)}
            />
          </div>
          <div style={COLUMNS}>
            {this.props.enableTaxTable && (
              <EbayInputs.UseTaxTable
                checked={form.useTaxTable}
                onChange={v => this.props.onFormChange('useTaxTable', v)}
              />
            )}
          </div>
          <div style={{ display: 'grid', gridGap: '20px', gridTemplateColumns: 'repeat(5, 1fr)' }} />
        </div>
      </div>
    )
  }
}
